import { logAmplitudeEvent } from 'amplitude/amplitude'
import type { DataProvider, Identifier } from 'ra-core'
import { useEffect, useState } from 'react'

import { fetchIdsForBulkEdit } from '../helpers'
import type { BulkEditDialogOpenPayload, BulkEditListQuery } from '../types'

type Options = {
  resource: string
  selectedIds: Identifier[] | undefined
  listQuery: BulkEditListQuery | undefined
  dataProvider: DataProvider
  translate: (key: string) => string
}

export function useBulkEditResolvedIds(options: Options) {
  const { resource, selectedIds, listQuery, dataProvider, translate } = options

  const explicitIds = selectedIds?.length ? selectedIds : []
  const needsResolve = explicitIds.length === 0 && Boolean(listQuery)

  const [resolvedIds, setResolvedIds] = useState<Identifier[]>([])
  const [resolving, setResolving] = useState(needsResolve)
  const [resolveError, setResolveError] = useState<string | null>(null)

  useEffect(() => {
    if (!needsResolve || !listQuery) {
      setResolving(false)
      return
    }
    let cancelled = false
    setResolving(true)
    setResolveError(null)
    fetchIdsForBulkEdit(dataProvider, resource, listQuery)
      .then((ids) => {
        if (cancelled) return
        setResolvedIds(ids)
        setResolving(false)
      })
      .catch(() => {
        if (cancelled) return
        logAmplitudeEvent('list_bulk_edit_modal_resolve_failed', { resource })
        setResolveError(translate('Could not resolve which records to update.'))
        setResolving(false)
      })
    return () => {
      cancelled = true
    }
  }, [dataProvider, listQuery, needsResolve, resource, translate])

  const effectiveIds = explicitIds.length > 0 ? explicitIds : resolvedIds

  return { effectiveIds, resolving, resolveError }
}
